import {
  dumpFormSpecToToml,
  type FormField,
  type FormSpec,
  isTrapField,
  LENGTH_CONSTRAINED_FIELDS,
  makeTrapField,
  parseFormSpecToml,
  TEXT_ONLY_FIELDS,
} from '@/lib/form-specs';

export type FieldKind =
  | 'text'
  | 'textarea'
  | 'email'
  | 'number'
  | 'url'
  | 'tel'
  | 'select'
  | 'radio'
  | 'checkbox'
  | 'date'
  | 'datetime'
  | 'time'
  | 'file'
  | 'hidden'
  | 'trap';

export const PALETTE: { group: 'groupText' | 'groupChoices' | 'groupOther'; kinds: FieldKind[] }[] =
  [
    { group: 'groupText', kinds: ['text', 'textarea', 'email', 'number', 'url', 'tel'] },
    { group: 'groupChoices', kinds: ['select', 'radio', 'checkbox'] },
    { group: 'groupOther', kinds: ['date', 'datetime', 'time', 'file', 'hidden', 'trap'] },
  ];

const BASE_NAMES: Record<FieldKind, string> = {
  text: 'text',
  textarea: 'message',
  email: 'email',
  number: 'number',
  url: 'url',
  tel: 'phone',
  select: 'choice',
  radio: 'choice',
  checkbox: 'choices',
  date: 'date',
  datetime: 'datetime',
  time: 'time',
  file: 'file',
  hidden: 'hidden_value',
  trap: 'website',
};

/** The palette entry a field reads as; legacy `submit` and unknown types fall through as their raw type. */
export function kindOf(field: FormField): FieldKind | 'submit' | string {
  return isTrapField(field) ? 'trap' : field.field;
}

export const isChoice = (field: FormField) =>
  field.field === 'select' || field.field === 'radio' || field.field === 'checkbox';

/** Which controls the properties panel shows for a field. */
export function capabilities(field: FormField) {
  const type = field.field;
  const trap = isTrapField(field);
  const textInput = ['text', 'textarea', 'email', 'number', 'url', 'tel'].includes(type);
  return {
    trap,
    label: !trap && type !== 'hidden',
    placeholder: !trap && textInput,
    help: !trap && type !== 'hidden' && type !== 'submit',
    value: type === 'hidden',
    options: isChoice(field),
    lengthBounds: !trap && LENGTH_CONSTRAINED_FIELDS.includes(type),
    valueBounds: type === 'number',
    pattern: !trap && TEXT_ONLY_FIELDS.includes(type),
    allowedValues: !trap && ['text', 'email', 'url', 'tel', 'hidden'].includes(type),
    email: type === 'email' || (type === 'text' && field.is_email === true),
    url: type === 'url' || (type === 'text' && field.is_url === true),
    multiple: type === 'select' || type === 'checkbox' || type === 'file',
  };
}

export function uniqueName(base: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  if (!used.has(base)) return base;
  let n = 2;
  while (used.has(`${base}_${n}`)) n += 1;
  return `${base}_${n}`;
}

export function newField(kind: FieldKind, label: string, taken: Iterable<string>): FormField {
  const name = uniqueName(BASE_NAMES[kind], taken);
  if (kind === 'trap') return makeTrapField(name);
  const field: FormField = { name, field: kind };
  if (kind !== 'hidden') field.label = label;
  if (kind === 'email') field.is_email = true;
  if (kind === 'url') field.is_url = true;
  return field;
}

const isEmptyValue = (value: unknown) => value === undefined || value === '' || value === false;

/** Sets one attribute; empty strings, false and undefined remove it so the TOML stays minimal. */
export function setAttr<K extends keyof FormField>(
  field: FormField,
  attr: K,
  value: FormField[K]
): FormField {
  const next = { ...field };
  if (isEmptyValue(value)) {
    delete next[attr];
  } else {
    next[attr] = value;
  }
  return next;
}

export function patchField(spec: FormSpec, key: string, patch: Partial<FormField>): FormSpec {
  const current = spec.fields[key];
  if (!current) return spec;
  let next = current;
  for (const [attr, value] of Object.entries(patch) as [keyof FormField, unknown][]) {
    next = setAttr(next, attr, value as never);
  }
  return { ...spec, fields: { ...spec.fields, [key]: next } };
}

function rebuild(spec: FormSpec, entries: [string, FormField][]): FormSpec {
  return { ...spec, fields: Object.fromEntries(entries) };
}

export function addField(spec: FormSpec, key: string, field: FormField, after?: string): FormSpec {
  const entries = Object.entries(spec.fields);
  const idx = after === undefined ? -1 : entries.findIndex(([k]) => k === after);
  if (idx < 0) entries.push([key, field]);
  else entries.splice(idx + 1, 0, [key, field]);
  return rebuild(spec, entries);
}

/** The backend matches submissions by table key, so key and name move together. */
export function renameField(spec: FormSpec, oldKey: string, newName: string): FormSpec {
  return rebuild(
    spec,
    Object.entries(spec.fields).map(([k, f]) =>
      k === oldKey ? [newName, { ...f, name: newName }] : [k, f]
    )
  );
}

export function duplicateField(spec: FormSpec, key: string): { spec: FormSpec; key: string } {
  const source = spec.fields[key];
  if (!source) return { spec, key };
  const base = key.replace(/_\d+$/, '');
  const copyKey = uniqueName(base, Object.keys(spec.fields));
  return { spec: addField(spec, copyKey, { ...source, name: copyKey }, key), key: copyKey };
}

export function removeField(spec: FormSpec, key: string): FormSpec {
  return rebuild(
    spec,
    Object.entries(spec.fields).filter(([k]) => k !== key)
  );
}

export function moveField(spec: FormSpec, from: number, to: number): FormSpec {
  const entries = Object.entries(spec.fields);
  const [moved] = entries.splice(from, 1);
  entries.splice(to, 0, moved);
  return rebuild(spec, entries);
}

/** Without fields there is nothing to validate against, so the spec is saved empty. */
export function serializeSpec(spec: FormSpec): string {
  return Object.keys(spec.fields).length === 0 ? '' : dumpFormSpecToToml(spec);
}

/** The backend refuses a spec without `discard_additional_fields`, so the editor always writes it. */
export function withSettings(spec: FormSpec): FormSpec {
  if (spec.settings?.discard_additional_fields !== undefined) return spec;
  return { ...spec, settings: { ...spec.settings, discard_additional_fields: false } };
}

/** Compare by content, not formatting, so reopening an unchanged spec is not "unsaved". */
export function normalizeToml(text: string): string {
  const result = parseFormSpecToml(text);
  return result.ok ? serializeSpec(withSettings(result.spec)) : text;
}

/** Lines in the editor, commas on the wire (the backend splits on commas). */
export const listFromLines = (text: string) =>
  text
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean)
    .join(',');

export const linesFromList = (list?: string) =>
  list
    ? list
        .split(',')
        .map((s) => s.trim())
        .join('\n')
    : '';
